import { Observable, Subject, throwError } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseApiService } from 'projects/core/src/lib/services/base-api.service';
import { AcStatusSettingsExerciseType, AcStatusTimingsType, AcStatusUserType } from '../models/acstatus/ac-status.type';

const RATING_TIMEOUT = 500;

@Injectable({
  providedIn: 'root'
})
export class ApiService extends BaseApiService {

  public rating$: Observable<any>;
  private ratingPayload = new Subject();

  public conference$: Observable<any>;
  private conferencePayload = new Subject();

  constructor(
    protected http: HttpClient
  ) {
    super(http);
    // set observable for ratings
    this.rating$ = this.ratingPayload.pipe(
      debounceTime(RATING_TIMEOUT),
      distinctUntilChanged(),
      switchMap((payload: any) => {
        return this.sendRatingToServer(payload);
      }),
    );
    this.conference$ = this.conferencePayload.pipe(
      debounceTime(RATING_TIMEOUT),
      distinctUntilChanged(),
      switchMap((payload: any) => {
        return this.sendConferenceDataToServer(payload);
      }),
    );
  }

  public getSetup(): Observable<any> {
    return this.http.get<any>(this.getUrl('setup'), this.options).pipe(
      catchError(err => {
        return throwError(err.error);
      })
    );
  }

  // AUTH MODERATORS

  public init(): Observable<any> {
    return this.http.get<any>(this.getUrl('init'), this.options).pipe(
      catchError(err => {
        return throwError(err.error);
      })
    );
  }

  public aclist(payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('aclist'), {
      email: payload.email,
      password: payload.password
    }, this.options).pipe(
      catchError(err => {
        return throwError(err.error);
      })
    );
  }

  public login(payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('login'), {
      email: payload.email,
      password: payload.password,
      ac: payload.ac
    }, this.options).pipe(
      catchError(err => {
        return throwError(err.error);
      })
    );
  }

  public aclistWithToken(): Observable<any> {
    return this.http.get<any>(this.getUrl('ac/list'), this.options).pipe(
      catchError(err => {
        return throwError(err.error);
      })
    );
  }

  public changeAc(id: number): Observable<any> {
    return this.http.post<any>(this.getUrl('ac/change'), { ac: id }, this.options).pipe(
      catchError(err => {
        return throwError(this.buildError(err));
      })
    );
  }

  // AUTH CANDIDATES/OBSERVERS

  public checkhash(type: string, hash: string): Observable<any> {
    return this.http
      .post<any>(this.getUrl('checkhash'), {
        type,
        hash
      })
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  public sendpincode(type: string, hash: string): Observable<any> {
    return this.http
      .post<any>(this.getUrl('sendpincode'), {
        type,
        hash
      })
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  public acceptPrivacyPolicy(hash: string): Observable<any> {
    return this.http
      .post<any>(this.getUrl('privacypolicy'), {
        hash
      })
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  public authenticate(type: string, hash: string, pincode: string): Observable<any> {
    return this.http
      .post<any>(this.getUrl('authenticate'), {
        type,
        hash,
        pincode,
      })
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  public authenticateDirect(type: string, hash: string, directhash: string, acid: number | null = null): Observable<any> {
    return this.http
      .post<any>(this.getUrl('authenticate-direct'), {
        type,
        hash,
        directhash,
        acid
      })
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  public sendOnetimePassword(email: string): Observable<any> {
    return this.http.post<any>(this.getUrl('onetime-password'), { email }, this.options).pipe(
      catchError(err => {
        return throwError(this.buildError(err));
      })
    );
  }

  public renewPassword(payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('renew-password'), payload, this.options).pipe(
      catchError(err => {
        return throwError(this.buildError(err));
      })
    );
  }

  public changePassword(payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('change-password'), payload, this.options).pipe(
      catchError(err => {
        return throwError(this.buildError(err));
      })
    );
  }

  // LOGOUT

  public logout(): Observable<any> {
    return this.http
      .post<any>(this.getUrl('logout'), {})
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  // DATA

  public getAc(): Observable<any> {
    return this.http
      .get<any>(this.getUrl('ac'))
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  public startAc(): Observable<any> {
    return this.http
      .post<any>(this.getUrl('ac/start'), {})
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  public finishAc(): Observable<any> {
    return this.http
      .post<any>(this.getUrl('ac/finish'), {})
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  public terminateAc(): Observable<any> {
    return this.http
      .post<any>(this.getUrl('ac/terminate'), {})
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  public getStatus(userStatus: AcStatusUserType): Observable<any> {
    return this.http
      .post<any>(this.getUrl('status'), {
        status: userStatus
      })
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  public setStatusSettings(exerciseId: number, settings: AcStatusSettingsExerciseType): Observable<any> {
    return this.http.post<any>(this.getUrl('status/settings'), {
      id: exerciseId,
      settings
    }).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public setStatusTiming(hash: string, timing: AcStatusTimingsType): Observable<any> {
    return this.http.post<any>(this.getUrl('status/timing'), {
      hash,
      timing
    }).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public setNotice(notices: { [key: string]: string } | null): Observable<any> {
    return this.http.post<any>(this.getUrl('notice'), {
      notices
    }).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public getRatings(exercise: number): Observable<any> {
    return this.http.get<any>(this.getUrl('ratings/' + exercise))
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  public getOnlinetestRatings(onlinetest: number): Observable<any> {
    return this.http.get<any>(this.getUrl('ratings-onlinetests/' + onlinetest))
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  public setOnlinetestStarted(id: any): Observable<any> {
    return this.http.post<any>(this.getUrl('onlinetest/start'), { id }).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public setRating(mode: string, owner: number, candidate: string, rating: any, comments: string | null = null): void {
    this.ratingPayload.next({
      mode,
      candidate,
      onlinetest: (mode === 'onlinetest') ? owner : null,
      slot: (mode === 'exercise') ? owner : null,
      rating,
      comments
    });
  }

  private sendRatingToServer(payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('ratings'), payload).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public getReports(): Observable<any> {
    return this.http.get<any>(this.getUrl('reports'))
    .pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public getConferenceData(): Observable<any> {
    return this.http.get<any>(this.getUrl('conference'))
    .pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public setConferenceData(payload: any): void {
    this.conferencePayload.next(payload);
  }

  private sendConferenceDataToServer(payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('conference'), payload).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public saveMark(payload: any): Observable<any> {
    return this.http.post<any>(this.getUrl('mark'), payload).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public deleteMark(uuid: string): Observable<any> {
    return this.http.delete<any>(this.getUrl('mark/' + uuid)).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public downloadFile(type: string, owner_id: number, file_id: number): Observable<any> {
    const options = {...this.options};
    options.responseType = 'arraybuffer';
    return this.http.get<any>(this.getUrl('files/' + type + '/' + owner_id + '/' + file_id), options).pipe(
      catchError(err => {
        return throwError(this.buildError(err));
      })
    );
  }

  public saveTimezone(timezone: string): Observable<any> {
    return this.http.post<any>(this.getUrl('timezone'), { timezone }).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  // candidate moving

  public getMovingAcs(): Observable<any> {
    return this.http.get<any>(this.getUrl('moving/acs'))
    .pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public moveCandidateToOtherAc(ac: number, candidate: string): Observable<any> {
    return this.http.post<any>(this.getUrl('moving/candidate'), {
      ac,
      candidate
    })
    .pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  // result reports

  public getResultReport(): Observable<any> {
    return this.http
      .get<any>(this.getUrl('resultreports/status'))
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  public regenerateResultReport(id: any): Observable<any> {
    return this.http
      .post<any>(this.getUrl('resultreports/regenerate/' + id), {})
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }

  public finishResultReport(id: any): Observable<any> {
    return this.http
      .post<any>(this.getUrl('resultreports/finish/' + id), {})
      .pipe(
        catchError(err => {
          return throwError(err);
        })
      );
  }


  public uploadResultReport(id: any, file: File): Observable<any> {
    const headers = new HttpHeaders();
    headers.append('Accept', 'application/json');
    headers.append('Content-Type', 'multipart/form-data');
    const payload: FormData = new FormData();
    payload.append('file', file);
    return this.http.post<any>(this.getUrl('resultreports/upload/' + id), payload, {
      headers
    }).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }


  // avatar

  uploadAvatar(files: FileList): Observable<any> {
    const headers = new HttpHeaders();
    headers.append('Accept', 'application/json');
    headers.append('Content-Type', 'multipart/form-data');
    const payload: FormData = new FormData();
    Array.from(files).forEach((file: File, index: number) => {
      payload.append('files_' + index, file);
    });
    return this.http.post<any>(this.getUrl('avatar'), payload, {
      headers
    }).pipe(
      catchError((error) => throwError(error))
    );
  }

  deleteAvatar(): Observable<any> {
    return this.http.delete<any>(this.getUrl('avatar')).pipe(
      catchError((error) => throwError(error))
    );
  }

}
